/**
 * PlatformTour — First-time user onboarding with optional DOM spotlights.
 *
 * Welcome opens on Dashboard for new users (localStorage flag). Continuing
 * navigates to Workbench and spotlights real UI (Omnibar, canvas, Inspector…).
 * Skip / Get Started / close mark the tour as seen (username-scoped when possible).
 *
 * Active step is persisted in sessionStorage so Dashboard → Workbench never
 * drops the instruction card. Backdrop clicks do not dismiss the tour.
 */
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
    Network, Search, BarChart3, FileText,
    Eye, Zap, ChevronRight, ChevronLeft, X, Rocket, Keyboard, Tags, Crosshair, Undo2, GripVertical,
} from 'lucide-react';
import { brandPrimaryBtn } from './brandChrome';
import { TourSpotlight, type TourPlacement } from './tour/TourSpotlight';
import { findUiElement, getUiLocation, type UiLocation, type UiLocationKey } from './tour/uiLocations';
import { ensureUiReady, releaseUiSurfaces } from './tour/uiLocator';

const STORAGE_PREFIX = 'cryptoflow-tour-seen';
const ACTIVE_KEY = 'cryptoflow-tour-active';
const FORCE_OPEN_EVENT = 'cryptoflow-force-tour';

/**
 * One bullet in a step's detail list.
 *
 * `locate` is what makes the list navigable rather than merely descriptive. A
 * bullet like "Trace runs Follow the Money" named a feature the
 * reader then had to hunt for — the control lives in the header toolbar and
 * the colour is only explained in a legend behind a menu. With a registry key
 * the line points at itself: click it and the spotlight moves to the real
 * control, or explains the click path when that control is not on screen yet.
 */
type TourDetail = {
    text: string;
    locate?: UiLocationKey;
};

const d = (text: string, locate?: UiLocationKey): TourDetail => ({ text, locate });

type TourStep = {
    icon: React.ElementType;
    title: string;
    description: string;
    details: TourDetail[];
    /** CSS selector; null = centered modal (no spotlight). */
    target: string | null;
    placement?: TourPlacement;
    /** When true, Next from this step navigates to Workbench before advancing. */
    navigateToWorkbench?: boolean;
};

const TOUR_STEPS: TourStep[] = [
    {
        icon: Rocket,
        title: 'Welcome to CryptoFlow',
        description: 'Start with a quick, case-free look from the top bar. Paste an address or transaction hash and CryptoFlow opens it on the Workbench canvas without creating a case.',
        details: [
            d('Top bar Investigate: paste an address, transaction hash, or UPI, then select Investigate (or press Enter)', 'global-omnibar'),
            d('Start here: three guided demos on this Dashboard (~25 min)', 'dashboard-start-here'),
            d('Demo library: Graph Legend and the rest of the catalog (sidebar Demos, or Open demo library)', 'dashboard-demos'),
            d('Section chips jump to Overview, Quick Access, Cases, and Start here', 'dashboard-section-nav'),
            d('This loads an uncased Workbench canvas for exploration — it does not create or save a case'),
            d('On the Workbench, use the Omnibar to add more addresses or transaction hashes to that canvas'),
        ],
        target: '[data-tour="dashboard-start-here"]',
        placement: 'bottom',
        navigateToWorkbench: true,
    },
    {
        icon: Search,
        title: 'Omnibar — Start Here',
        description: 'The search bar at the top of the Workbench. Paste a wallet address, transaction hash, or UPI ID.',
        details: [
            d('Chain is auto-detected (T... = TRX, 0x... = ETH, bc1... = BTC)', 'omnibar-chain-badge'),
            d('Addresses load onto the canvas as nodes', 'omnibar-add'),
            d('Transaction hashes show the sender and receiver', 'graph-canvas'),
        ],
        target: '[data-tour="omnibar"]',
        placement: 'bottom',
    },
    {
        icon: Network,
        title: 'Graph Canvas — Your Investigation Map',
        description:
            'This centre panel is the case map. Every box (node) is a wallet address; every line (edge) is a real on-chain transfer moving value between wallets. Build the map from the Omnibar, then click through it to investigate.',
        details: [
            d('Click a node → the Node Inspector (right panel) opens with that wallet’s details', 'node-inspector'),
            d('Right-click a node → expand neighbours, run a trace, or flag for review', 'node-context-menu'),
            // Point at the map itself — not the +/- toolbar — so the spotlight
            // cutout leaves the canvas open for wheel-zoom and empty-space pan.
            d('Scroll wheel = zoom · drag empty space = pan around the map', 'graph-canvas'),
            // Lead with the button name — Show me spotlights header Trace, not a gold edge.
            d('Trace (header toolbar) runs Follow the Money — gold trails on the map are the fund path to an exchange or cash-out', 'header-trace'),
            d('Legend (header toolbar) opens the Graph Legend popup — drag it, resize from any edge, or close it when done', 'canvas-legend'),
            d('Filters / eye (header) restore dimmed or hidden nodes', 'graph-filters'),
            d('Recompute and Rearrange as (bottom of canvas) tidy the layout — they never change the underlying data', 'canvas-layout-modes'),
            d('Empty canvas? Start with Demo loads a training graph before free exploration', 'canvas-empty-demo'),
            d('Show Fund Flow Playback (below the canvas) replays transfers chronologically for court / training walkthroughs', 'canvas-playback'),
        ],
        target: '[data-testid="wb-graph-canvas"]',
        placement: 'left',
    },
    {
        icon: Eye,
        title: 'Node Inspector — Details for the Selected Wallet',
        description:
            'The right-hand panel. Click any wallet on the canvas and this panel fills with identity, risk, transfers, and investigation tools for that address. A one-line explanation sits under the tab row telling you what the open tab shows; hover the ⓘ icons elsewhere for more detail.',
        details: [
            d('Seven tabs, left to right: Overview, Parties, Transfers, Intel, Analysis, Actions, Forensic', 'inspector-tabs'),
            d('Overview — risk score, entity attribution, balances', 'inspector-tab-overview'),
            d('Parties — who this wallet traded with (counterparties)', 'inspector-tab-parties'),
            d('Transfers — transaction history; expand more hops onto the canvas', 'inspector-tab-transfers'),
            d('Intel — sanctions, profile, exposure, bridges, P2P', 'inspector-tab-intel'),
            d('Analysis — Auto-Investigate and graph-wide algorithms', 'inspector-tab-analysis'),
            d('Actions — flag, evidence, and case workflows', 'inspector-tab-actions'),
            d('Forensic — subpoena records an exchange disclosed for this wallet', 'inspector-tab-forensic'),
            d('Narrow panel? The tabs wrap onto a second row — drag the panel’s left edge for one row', 'inspector-resize'),
        ],
        target: '[data-tour="node-inspector"]',
        placement: 'left',
    },
    {
        icon: Tags,
        title: 'Node Identity Chips',
        description: 'Inside the Node Inspector identity card. Instant triage signals for the selected address.',
        details: [
            d('Risk score with color coding (green/amber/red)', 'chip-risk'),
            d('OFAC sanctions check when a hit is found', 'chip-sanctions'),
            d('Entity attribution (exchange, mixer, labels)', 'identity-chips'),
            d('Nearest exchange hop count, bridges, and escalation', 'chip-nearest-exchange'),
            d('Escalation recommendation once an investigation has run', 'chip-escalation'),
        ],
        target: '[data-tour="identity-chips"]',
        placement: 'left',
    },
    {
        icon: Zap,
        title: 'Analysis Algorithms',
        description: 'Docked in the Node Inspector Analysis tab. Run automated analysis workflows.',
        details: [
            d('Auto-Investigate: Targeted node intelligence & entity evaluation', 'analysis-auto-investigate'),
            d('Follow the Money: draws the gold trail to the nearest exchange', 'analysis-follow-money'),
            d('Macro Graph Analysis: Fraud pattern detection (pig butchering, peel chains)', 'analysis-macro'),
            d('Signal assessment with AI-generated executive summaries', 'le-report'),
            d('Results stream live via SSE — watch the canvas populate', 'graph-canvas'),
        ],
        target: '[data-tour="analysis-tab"]',
        placement: 'left',
    },
    {
        icon: BarChart3,
        title: 'Granular Control',
        description: 'Use the granular action row below the Node Inspector header.',
        details: [
            d('Highlight related paths and counterparties instantly', 'granular-actions'),
            d('Hide or show nodes dynamically to unclutter the canvas', 'header-show-all'),
            d('Run single-step traces and entity lookups per node', 'granular-follow-money'),
            d('Taken a wrong turn? Undo and Redo cover every canvas change', 'header-undo-redo'),
        ],
        target: '[data-tour="granular-actions"]',
        placement: 'left',
    },
    {
        icon: FileText,
        title: 'Cases & Reports',
        description: 'Save investigations to cases. Generate LE reports for court submission.',
        details: [
            d('Cases page — manage FIRs, link investigations', 'nav-cases'),
            d('Evidence | Consolidated — a large case-worker canvas opens on Consolidated; switch to Evidence for the full wallet map', 'case-consolidated-toggle'),
            d('Clear focus restores every wallet after a consolidated bucket is shown on the evidence canvas', 'case-clear-focus'),
            d('CSV import — bulk load wallet addresses', 'cases-csv-import'),
            d('Auto-save — investigations persist across sessions'),
            d('PDF report with Section 65B compliance citations', 'le-report'),
        ],
        target: '[data-tour="nav-cases"]',
        placement: 'right',
    },
    {
        icon: Keyboard,
        title: 'Keyboard Shortcuts',
        description: 'Speed up your workflow with keyboard shortcuts.',
        details: [
            d('I — Open Analysis → Targeted Node Intelligence', 'analysis-auto-investigate'),
            d('G — Open Analysis → Macro Graph Analysis', 'analysis-macro'),
            d('? or F1 — Show all keyboard shortcuts', 'canvas-shortcuts'),
            d('Right-click — Node/edge context menu', 'node-context-menu'),
            d('Scroll — Zoom in/out on canvas', 'graph-canvas'),
        ],
        target: null,
    },
];

function getUsername(): string | null {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { username?: string };
        return parsed?.username || null;
    } catch {
        return null;
    }
}

export function getTourStorageKey(): string {
    const user = getUsername();
    return user ? `${STORAGE_PREFIX}:${user}` : STORAGE_PREFIX;
}

export function hasSeenTour(): boolean {
    const scoped = getTourStorageKey();
    if (localStorage.getItem(scoped) === 'true') return true;
    if (localStorage.getItem(STORAGE_PREFIX) === 'true') {
        if (scoped !== STORAGE_PREFIX) {
            localStorage.setItem(scoped, 'true');
        }
        return true;
    }
    return false;
}

function markTourSeen() {
    localStorage.setItem(getTourStorageKey(), 'true');
}

/** In-progress tour step (survives Dashboard → Workbench navigation). */
export function getActiveTourStep(): number | null {
    try {
        const raw = sessionStorage.getItem(ACTIVE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { step?: number };
        if (typeof parsed.step === 'number' && parsed.step >= 0 && parsed.step < TOUR_STEPS.length) {
            return parsed.step;
        }
        return null;
    } catch {
        return null;
    }
}

export function setActiveTourStep(step: number) {
    sessionStorage.setItem(ACTIVE_KEY, JSON.stringify({ step }));
}

export function clearActiveTour() {
    sessionStorage.removeItem(ACTIVE_KEY);
}

/** Reset tour so it shows again (seen flags + in-progress session). */
export function resetTour() {
    localStorage.removeItem(getTourStorageKey());
    localStorage.removeItem(STORAGE_PREFIX);
    clearActiveTour();
}

/** Open the tour from Header Help / Settings Retake (works with Layout-mounted instance). */
export function requestTourOpen() {
    window.dispatchEvent(new CustomEvent(FORCE_OPEN_EVENT));
}

const LOCATE_HINT_W = 320;

/**
 * Side panel for Show me when the target is not on screen yet.
 * Lives outside the tour card's scroll area so long bullet lists cannot bury
 * the where/reveal instructions (right-click menus, conditional tabs, etc.).
 */
function TourLocateHint({
    location,
    onDismiss,
}: {
    location: UiLocation;
    onDismiss: () => void;
}) {
    const [pos, setPos] = useState<{ top: number; left: number }>(() => ({
        top: 96,
        left: typeof window !== 'undefined'
            ? Math.max(16, window.innerWidth - LOCATE_HINT_W - 16)
            : 16,
    }));

    useLayoutEffect(() => {
        const place = () => {
            const dialog = document.querySelector<HTMLElement>('[role="dialog"][data-tour-mode]');
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const r = dialog?.getBoundingClientRect();
            if (!r) {
                setPos({
                    top: 96,
                    left: Math.max(16, vw - LOCATE_HINT_W - 16),
                });
                return;
            }
            const preferRight = r.right + 12 + LOCATE_HINT_W <= vw - 16;
            const left = preferRight
                ? r.right + 12
                : Math.max(16, r.left - LOCATE_HINT_W - 12);
            const top = Math.max(16, Math.min(r.top, vh - 220));
            setPos({ top, left });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [location]);

    return createPortal(
        <aside
            className="fixed z-[220] w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-primary/30 bg-card shadow-2xl pointer-events-auto animate-in fade-in slide-in-from-left-2 duration-200"
            style={{ top: pos.top, left: pos.left }}
            data-testid="tour-locate-note"
            role="status"
            aria-live="polite"
        >
            <div className="flex items-start gap-2 px-3.5 py-3 border-b border-border bg-brand-ink rounded-t-xl">
                <Crosshair className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" aria-hidden />
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wide text-white/55 font-medium">How to find it</p>
                    <p className="text-sm font-semibold text-white leading-tight">{location.label}</p>
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss instructions"
                    className="shrink-0 p-1 -m-1 rounded text-white/55 hover:text-white hover:bg-white/10 transition-colors"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
            <div className="px-3.5 py-3 space-y-2">
                <p className="text-xs leading-relaxed text-foreground">{location.where}</p>
                {location.reveal && (
                    <p className="text-xs leading-relaxed break-words text-amber-800 dark:text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-md px-2.5 py-2">
                        {location.reveal}
                    </p>
                )}
                <button
                    type="button"
                    onClick={onDismiss}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                    <Undo2 className="w-3 h-3" aria-hidden />
                    Back to tour step
                </button>
            </div>
        </aside>,
        document.body,
    );
}

export function PlatformTour({
    forceOpen: forceOpenProp,
    onClose: onCloseCallback,
}: {
    forceOpen?: boolean;
    onClose?: () => void;
} = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [forced, setForced] = useState(Boolean(forceOpenProp));
    const bootstrapped = useRef(false);
    /** Bullet the reader asked to be shown, if any. Cleared on step change. */
    const [focus, setFocus] = useState<{ key: UiLocationKey; present: boolean } | null>(null);

    const clearFocus = useCallback(() => {
        // Filters portal was opened by Show me — close it when leaving that bullet.
        releaseUiSurfaces();
        setFocus(null);
    }, []);

    const goToStep = useCallback((next: number) => {
        const clamped = Math.max(0, Math.min(next, TOUR_STEPS.length - 1));
        // Leaving a step (or jumping via tabs) must dismiss tour-opened overlays
        // so the next step does not inherit an open Filters dropdown.
        releaseUiSurfaces();
        setStep(clamped);
        setActiveTourStep(clamped);
        setFocus(null);
    }, []);

    const focusDetail = useCallback((key: UiLocationKey) => {
        // Close Filters when moving to a different Show me; re-open if this key needs it.
        releaseUiSurfaces(key);
        // Open buried panels (Graph Legend, …) before we look for them, so
        // Show me rings the real UI instead of falling back to a click-path note.
        ensureUiReady(key);
        setFocus({ key, present: Boolean(findUiElement(key)) });
        // Panel may mount on the next paint — re-check shortly.
        window.requestAnimationFrame(() => {
            setFocus(prev => (prev?.key === key
                ? { key, present: Boolean(findUiElement(key)) }
                : prev));
        });
    }, []);

    // A focused element can appear or disappear while the reader looks at it —
    // opening the Analysis tab renders its cards, changing node clears chips.
    // Re-check so the spotlight takes over as soon as the target exists.
    useEffect(() => {
        if (!focus) return;
        const id = window.setInterval(() => {
            const present = Boolean(findUiElement(focus.key));
            setFocus(prev => (prev && prev.key === focus.key && prev.present !== present
                ? { key: prev.key, present }
                : prev));
        }, 600);
        return () => window.clearInterval(id);
    }, [focus]);

    const clearTourQuery = useCallback(() => {
        if (!searchParams.has('tour')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('tour');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const handleClose = useCallback(() => {
        releaseUiSurfaces();
        setIsOpen(false);
        setForced(false);
        markTourSeen();
        clearActiveTour();
        clearTourQuery();
        onCloseCallback?.();
    }, [clearTourQuery, onCloseCallback]);

    const focusTourCard = useCallback(() => {
        const el = document.querySelector<HTMLElement>('[role="dialog"][data-tour-mode]');
        el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        el?.focus?.();
    }, []);

    // Bootstrap / resume
    useEffect(() => {
        if (forceOpenProp) {
            setForced(true);
            setIsOpen(true);
            goToStep(0);
            return;
        }
        if (forced) return;

        if (hasSeenTour()) {
            clearActiveTour();
            return;
        }

        const activeStep = getActiveTourStep();
        const resumeFromQuery = searchParams.get('tour') === '1';
        const onTourRoute =
            location.pathname === '/' ||
            location.pathname.startsWith('/workbench');

        if (activeStep !== null) {
            setIsOpen(true);
            setStep(activeStep);
            if (activeStep >= 1 && !location.pathname.startsWith('/workbench')) {
                navigate('/workbench?tour=1');
            }
            return;
        }

        if (resumeFromQuery) {
            setIsOpen(true);
            goToStep(Math.max(1, activeStep ?? 1));
            return;
        }

        if (onTourRoute && !bootstrapped.current) {
            bootstrapped.current = true;
            setIsOpen(true);
            goToStep(0);
        }
    }, [forceOpenProp, forced, location.pathname, searchParams, navigate, goToStep]);

    useEffect(() => {
        const onForce = () => {
            clearActiveTour();
            setForced(true);
            setIsOpen(true);
            goToStep(0);
        };
        window.addEventListener(FORCE_OPEN_EVENT, onForce);
        return () => window.removeEventListener(FORCE_OPEN_EVENT, onForce);
    }, [goToStep]);

    useEffect(() => {
        if (!isOpen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen]);

    // Keep session in sync whenever step/open changes
    useEffect(() => {
        if (isOpen) setActiveTourStep(step);
    }, [isOpen, step]);

    const handleNext = () => {
        const current = TOUR_STEPS[step];
        if (step < TOUR_STEPS.length - 1) {
            const nextIndex = step + 1;
            if (current.navigateToWorkbench && !location.pathname.startsWith('/workbench')) {
                goToStep(nextIndex);
                navigate('/workbench?tour=1');
                return;
            }
            goToStep(nextIndex);
        } else {
            handleClose();
        }
    };

    const handlePrev = () => {
        if (step <= 0) return;
        const prevIndex = step - 1;
        if (prevIndex === 0 && location.pathname.startsWith('/workbench')) {
            goToStep(0);
            clearTourQuery();
            navigate('/');
            return;
        }
        goToStep(prevIndex);
    };

    if (!isOpen) return null;

    const current = TOUR_STEPS[step];
    const Icon = current.icon;
    const isLast = step === TOUR_STEPS.length - 1;
    const progressPct = ((step + 1) / TOUR_STEPS.length) * 100;
    const focusLocation = focus ? getUiLocation(focus.key) : null;
    // A focused bullet borrows the spotlight; the step's own target is where it rests.
    const showingFocus = Boolean(focus?.present && focusLocation);
    const spotlightTarget = showingFocus && focusLocation ? focusLocation.selector : current.target;

    return (
        <>
            <TourSpotlight
                targetSelector={spotlightTarget}
                placement={showingFocus ? 'auto' : (current.placement ?? 'auto')}
                positionKey={step}
            >
                <button
                    onClick={handleClose}
                    className="absolute top-3 right-3 z-10 p-1.5 rounded-full border border-white/15 bg-brand-ink/80 text-white/70 hover:text-white hover:bg-brand-ink transition-colors shadow-sm"
                    title="Close"
                    aria-label="Close tour"
                >
                    <X className="w-4 h-4" />
                </button>

                <div
                    className="bg-brand-ink px-5 pt-4 pb-3 pr-12 border-b-4 border-b-primary shrink-0 cursor-grab active:cursor-grabbing select-none"
                    data-tour-drag-handle
                    title="Drag to move"
                >
                    <div className="flex items-center gap-2.5 mb-2">
                        <GripVertical className="w-3.5 h-3.5 text-white/40 shrink-0" aria-hidden />
                        <span className="p-1.5 rounded-md bg-primary/15 shrink-0">
                            <Icon className="w-4 h-4 text-primary" />
                        </span>
                        <span className="text-xs text-white/65 font-medium">
                            Step {step + 1} of {TOUR_STEPS.length}
                        </span>
                    </div>
                    <h2 id="platform-tour-title" className="text-lg font-semibold text-white leading-tight">
                        {current.title}
                    </h2>
                </div>

                <div className="h-1 bg-secondary shrink-0">
                    <div
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${progressPct}%` }}
                    />
                </div>

                <div className="px-5 py-4 overflow-y-auto styled-scrollbar bg-card flex-1 min-h-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">{current.description}</p>

                    {current.details.some(detail => detail.locate) && (
                        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <Crosshair className="w-3 h-3 text-primary shrink-0" aria-hidden />
                            Click any line below to point it out on screen.
                        </p>
                    )}

                    <ul className="mt-1.5 space-y-1 rounded-lg border border-primary/20 bg-brand-cream/40 dark:bg-primary/5 p-2.5">
                        {current.details.map((detail, i) => {
                            const isFocused = focus?.key === detail.locate;
                            if (!detail.locate) {
                                return (
                                    <li key={i} className="flex items-start gap-2 px-1 py-1 text-xs text-foreground/90">
                                        <ChevronRight className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" aria-hidden />
                                        <span>{detail.text}</span>
                                    </li>
                                );
                            }
                            return (
                                <li key={i}>
                                    <button
                                        type="button"
                                        onClick={() => focusDetail(detail.locate as UiLocationKey)}
                                        aria-pressed={isFocused}
                                        aria-label={`Locate on screen: ${getUiLocation(detail.locate).label}`}
                                        title={`Locate on screen — ${getUiLocation(detail.locate).label}`}
                                        className={`group w-full flex items-start gap-2 text-left px-1 py-1 rounded-md text-xs transition-colors min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                                            isFocused
                                                ? 'bg-primary/15 text-foreground'
                                                : 'text-foreground/90 hover:bg-primary/10'
                                        }`}
                                    >
                                        <ChevronRight className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" aria-hidden />
                                        <span className="flex-1 min-w-0 break-words">{detail.text}</span>
                                        <span
                                            className={`shrink-0 mt-0.5 inline-flex items-center text-primary transition-opacity ${
                                                isFocused
                                                    ? 'opacity-100'
                                                    : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
                                            }`}
                                            aria-hidden
                                        >
                                            <Crosshair className="w-3 h-3" />
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    {showingFocus && focusLocation && (
                        <button
                            type="button"
                            onClick={clearFocus}
                            className="mt-2.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <Undo2 className="w-3 h-3" aria-hidden />
                            Showing {focusLocation.label} — back to {current.title.split(' — ')[0]}
                        </button>
                    )}
                </div>

                <div className="flex items-center justify-center gap-1.5 py-2.5 shrink-0" role="tablist" aria-label="Tour steps">
                    {TOUR_STEPS.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            role="tab"
                            aria-selected={i === step}
                            aria-label={`Go to step ${i + 1}`}
                            onClick={() => {
                                if (i >= 1 && !location.pathname.startsWith('/workbench')) {
                                    goToStep(i);
                                    navigate('/workbench?tour=1');
                                    return;
                                }
                                if (i === 0 && location.pathname.startsWith('/workbench')) {
                                    goToStep(0);
                                    clearTourQuery();
                                    navigate('/');
                                    return;
                                }
                                goToStep(i);
                            }}
                            className={`h-1.5 rounded-full transition-all ${
                                i === step
                                    ? 'w-5 bg-primary'
                                    : i < step
                                        ? 'w-2 bg-primary/45 hover:bg-primary/70'
                                        : 'w-2 bg-border hover:bg-muted-foreground/40'
                            }`}
                        />
                    ))}
                </div>

                <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-secondary/30 dark:bg-secondary/20 shrink-0">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                        Skip tour
                    </button>
                    <div className="flex items-center gap-2">
                        {step > 0 && (
                            <button
                                type="button"
                                onClick={handlePrev}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-foreground border border-border rounded-lg bg-secondary hover:bg-muted transition-colors"
                            >
                                <ChevronLeft className="w-3 h-3" /> Back
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleNext}
                            className={`flex items-center gap-1 px-4 py-1.5 text-xs rounded-lg ${brandPrimaryBtn}`}
                        >
                            {isLast ? 'Get Started' : 'Next'} <ChevronRight className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </TourSpotlight>

            {/* Absent-target Show me → pop out beside the tour card (not buried under bullets). */}
            {focus && focusLocation && !focus.present && (
                <TourLocateHint location={focusLocation} onDismiss={clearFocus} />
            )}

            {/* Always-visible progress chip — sequence stays obvious if card is hard to spot */}
            <div
                className="fixed bottom-4 right-4 z-[210] flex items-center gap-2 rounded-lg border border-border bg-card/95 shadow-lg px-3 py-2 backdrop-blur-sm"
                data-testid="tour-progress-chip"
            >
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Tour · Step {step + 1} of {TOUR_STEPS.length}
                </span>
                <button
                    type="button"
                    onClick={focusTourCard}
                    className="text-xs font-medium text-primary hover:underline"
                >
                    Continue
                </button>
                <button
                    type="button"
                    onClick={handleClose}
                    className="text-xs text-muted-foreground hover:text-foreground"
                >
                    Skip
                </button>
            </div>
        </>
    );
}
